import { connect } from 'react-redux';
import {
    Text,
    Avatar,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Spinner,
    TableCellLayout,
    TableColumnDefinition,
    Toolbar,
    ToolbarButton,
    Button,
    TeachingPopover,
    TeachingPopoverTrigger,
    TeachingPopoverSurface,
    TeachingPopoverHeader,
    TeachingPopoverBody
} from '@fluentui/react-components';
import { useEffect, useMemo, useState } from 'react';

// styles
import './Payments.List.Styles.css';

//icons
import {
    Add16Regular,
    EditRegular,
    DeleteRegular,
    Chat20Regular,
    DocumentBulletList20Regular,
    ArrowImport20Regular,
    CalendarLtr20Regular,
    CheckmarkCircle20Regular,
    DismissCircle20Regular
} from '@fluentui/react-icons';

// Component functions
import * as ComponentFunctions from './Payments.List.Functions';

// models
import { Payment } from '../../../../models/Entities/CashFlow/Payment/Payment';

// components
import PaymentFormComponent from '../Payment.Form/Payment.Form.Component';
import { FormType } from '../Payment.Form/Payment.Form.Function';
import PaymentImputationFormComponent from '../PaymentImputation.Form/PaymentImputation.Form.Component';
import PaymentImputationsViewComponent from '../PaymentImputations.View/PaymentImputations.View.Component';
import moment from 'moment';

const formatAmount = (amount: number | undefined, currency: string | undefined): string => {
    try {
        return (amount ?? 0).toLocaleString('es-AR', {
            style: 'currency',
            currency: (currency || 'ARS').toUpperCase()
        });
    } catch {
        return `${(currency || 'ARS').toUpperCase()} ${(amount ?? 0).toFixed(2)}`;
    }
};

const PaymentListComponent = (props: ComponentFunctions.Props) => {

    // add or update form control
    const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

    // imputaciones (vista)
    const [selectedViewPayment, setSelectedViewPayment] = useState<Payment | null>(null);

    // imputar saldo pendiente (formulario)
    const [selectedImputationPayment, setSelectedImputationPayment] = useState<Payment | null>(null);

    const isPayer = props.type === ComponentFunctions.PaymentListType.Payer;

    // events
    const onPaymentFormDismiss = () => {
        setSelectedPayment(null);
        setIsPaymentFormOpen(false);
    };

    const onEditPayment = (payment: Payment) => {
        setSelectedPayment(payment);
        setIsPaymentFormOpen(true);
    };

    const onDeletePayment = (payment: Payment) => {
        props.DeletePayment(payment.id);
    };

    useEffect(() => {
        props.GetAllPersons();
        switch (props.type) {
            case ComponentFunctions.PaymentListType.Payer:
                props.GetAllPaymentsByPayer(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
                break;
            case ComponentFunctions.PaymentListType.Receiver:
                props.GetAllPaymentsByReceiver(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalCount = props.Payments?.length ?? 0;

    const Forms = (
        <PaymentFormComponent
            FormType={isPayer ? FormType.payer : FormType.receiver}
            ReferalId={props.EntityIdentifier}
            FixedAssociatedEntityIdentifier={props.FixedAssociatedEntityIdentifier}
            isOpen={isPaymentFormOpen}
            PaymentId={selectedPayment?.id}
            onClose={onPaymentFormDismiss}
        />
    );

    const renderStatusPill = (item: Payment): JSX.Element => {
        const pending = item.pendingIndictmentAmount ?? 0;
        if (pending > 0) {
            return (
                <span className='paymentsList__statusPill paymentsList__statusPill--pending'>
                    <DismissCircle20Regular style={{ width: 14, height: 14 }} />
                    Con saldo
                </span>
            );
        }
        return (
            <span className='paymentsList__statusPill paymentsList__statusPill--cleared'>
                <CheckmarkCircle20Regular style={{ width: 14, height: 14 }} />
                Imputado
            </span>
        );
    };

    const columns: TableColumnDefinition<Payment>[] = useMemo(() => [
        createTableColumn<Payment>({
            columnId: 'payer',
            compare: (a, b) => {
                const an = props.GetPersonByIdentifier(a.payer)?.businessName || '';
                const bn = props.GetPersonByIdentifier(b.payer)?.businessName || '';
                return an.localeCompare(bn);
            },
            renderHeaderCell: () => 'Pagador',
            renderCell: (item: Payment) => {
                const name = props.GetPersonByIdentifier(item.payer)?.businessName;
                return (
                    <TableCellLayout
                        truncate
                        media={(
                            <Avatar
                                aria-label={item.payer}
                                name={name}
                                color='colorful'
                                size={28}
                            />
                        )}
                    >
                        {name}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Payment>({
            columnId: 'date',
            compare: (a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime(),
            renderHeaderCell: () => 'Fecha de pago',
            renderCell: (item: Payment) => (
                <TableCellLayout>
                    <span className='paymentsList__datePill'>
                        <CalendarLtr20Regular style={{ width: 14, height: 14, color: '#94a3b8' }} />
                        {moment(new Date(item.paymentDate).toLocaleDateString()).format('DD/MM/YYYY')}
                    </span>
                </TableCellLayout>
            )
        }),
        createTableColumn<Payment>({
            columnId: 'receiver',
            compare: (a, b) => {
                const an = props.GetPersonByIdentifier(a.receiver)?.businessName || '';
                const bn = props.GetPersonByIdentifier(b.receiver)?.businessName || '';
                return an.localeCompare(bn);
            },
            renderHeaderCell: () => 'Cobrador',
            renderCell: (item: Payment) => {
                const name = props.GetPersonByIdentifier(item.receiver)?.businessName;
                return (
                    <TableCellLayout
                        truncate
                        media={(
                            <Avatar
                                aria-label={item.receiver}
                                name={name}
                                color='colorful'
                                size={28}
                            />
                        )}
                    >
                        {name}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Payment>({
            columnId: 'status',
            renderHeaderCell: () => 'Estado',
            renderCell: (item: Payment) => (
                <TableCellLayout>
                    {renderStatusPill(item)}
                </TableCellLayout>
            )
        }),
        createTableColumn<Payment>({
            columnId: 'amount',
            compare: (a, b) => a.amount - b.amount,
            renderHeaderCell: () => 'Monto',
            renderCell: (item: Payment) => (
                <TableCellLayout>
                    <TeachingPopover>
                        <TeachingPopoverTrigger>
                            <Button
                                appearance='transparent'
                                size='small'
                                className='paymentsList__amount'
                                onClick={(e) => e.stopPropagation()}
                            >
                                {formatAmount(item.amount, item.currencyCode)}
                            </Button>
                        </TeachingPopoverTrigger>
                        <TeachingPopoverSurface>
                            <TeachingPopoverHeader>Detalle</TeachingPopoverHeader>
                            <TeachingPopoverBody>
                                <Text>{item.comments || 'Sin comentarios'}</Text>
                            </TeachingPopoverBody>
                        </TeachingPopoverSurface>
                    </TeachingPopover>
                </TableCellLayout>
            )
        }),
        createTableColumn<Payment>({
            columnId: 'imputedAmount',
            compare: (a, b) => (a.imputedAmount ?? 0) - (b.imputedAmount ?? 0),
            renderHeaderCell: () => 'Imputado',
            renderCell: (item: Payment) => (
                <TableCellLayout>
                    <span className='paymentsList__amount paymentsList__amount--imputed'>
                        {formatAmount(item.imputedAmount, item.currencyCode)}
                    </span>
                </TableCellLayout>
            )
        }),
        createTableColumn<Payment>({
            columnId: 'pendingIndictmentAmount',
            compare: (a, b) => (a.pendingIndictmentAmount ?? 0) - (b.pendingIndictmentAmount ?? 0),
            renderHeaderCell: () => 'Pendiente',
            renderCell: (item: Payment) => {
                const pending = item.pendingIndictmentAmount ?? 0;
                const cls = pending > 0
                    ? 'paymentsList__amount paymentsList__amount--pending'
                    : 'paymentsList__amount paymentsList__amount--cleared';
                return (
                    <TableCellLayout>
                        <span className={cls}>
                            {formatAmount(pending, item.currencyCode)}
                        </span>
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Payment>({
            columnId: 'comments',
            compare: (a, b) => (a.comments || '').localeCompare(b.comments || ''),
            renderHeaderCell: () => 'Comentarios',
            renderCell: (item: Payment) => {
                if (!item.comments) {
                    return (
                        <TableCellLayout>
                            <Text size={100} style={{ color: '#94a3b8' }}>—</Text>
                        </TableCellLayout>
                    );
                }
                return (
                    <TableCellLayout>
                        <TeachingPopover>
                            <TeachingPopoverTrigger>
                                <Button
                                    appearance='transparent'
                                    size='small'
                                    icon={<Chat20Regular />}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Ver
                                </Button>
                            </TeachingPopoverTrigger>
                            <TeachingPopoverSurface>
                                <TeachingPopoverHeader>Comentarios</TeachingPopoverHeader>
                                <TeachingPopoverBody>
                                    <Text>{item.comments}</Text>
                                </TeachingPopoverBody>
                            </TeachingPopoverSurface>
                        </TeachingPopover>
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Payment>({
            columnId: 'actions',
            renderHeaderCell: () => 'Acciones',
            renderCell: (item: Payment) => {
                const pending = item.pendingIndictmentAmount ?? 0;
                return (
                    <div className='paymentsList__actions'>
                        <Button
                            appearance='subtle'
                            size='small'
                            aria-label='Ver imputaciones'
                            icon={<DocumentBulletList20Regular />}
                            title='Ver imputaciones'
                            onClick={(event) => { event.stopPropagation(); setSelectedViewPayment(item); }}
                        />
                        {pending > 0 && (
                            <Button
                                appearance='subtle'
                                size='small'
                                aria-label='Imputar saldo pendiente'
                                icon={<ArrowImport20Regular style={{ color: '#c2410c' }} />}
                                title={`Imputar saldo pendiente: ${formatAmount(pending, item.currencyCode)}`}
                                onClick={(event) => { event.stopPropagation(); setSelectedImputationPayment(item); }}
                            />
                        )}
                        <Button
                            appearance='subtle'
                            size='small'
                            aria-label='Editar'
                            title='Editar'
                            icon={<EditRegular />}
                            onClick={(event) => { event.stopPropagation(); onEditPayment(item); }}
                        />
                        <Button
                            appearance='subtle'
                            size='small'
                            aria-label='Eliminar'
                            title='Eliminar'
                            icon={<DeleteRegular style={{ color: '#b91c1c' }} />}
                            onClick={(event) => { event.stopPropagation(); onDeletePayment(item); }}
                        />
                    </div>
                );
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [props.GetPersonByIdentifier]);

    const showEmpty = props.isLoadingSuccessfully && totalCount === 0;
    const wrapperClass = isPayer ? 'paymentsList paymentsList--payer' : 'paymentsList paymentsList--receiver';
    const badgeClass = isPayer ? 'paymentsList__titleBadge paymentsList__titleBadge--payer' : 'paymentsList__titleBadge paymentsList__titleBadge--receiver';
    const titleText = isPayer ? 'Pagos realizados' : 'Cobros recibidos';
    const subtitleText = isPayer ? 'Egresos consolidados del período' : 'Ingresos consolidados del período';
    const addLabel = isPayer ? 'Agregar pago' : 'Agregar cobro';

    return (
        <div className={wrapperClass}>
            <div className='paymentsList__header'>
                <div className='paymentsList__headerLeft'>
                    <span className='paymentsList__title'>
                        {titleText + ' '}
                        <span className={badgeClass}>{totalCount}</span>
                    </span>
                    <span className='paymentsList__subtitle'>
                        {subtitleText}
                    </span>
                </div>
                <div className='paymentsList__headerRight'>
                    <Toolbar aria-label='Acciones' className='paymentsList__toolbar'>
                        <ToolbarButton
                            aria-label={addLabel}
                            appearance='primary'
                            icon={<Add16Regular />}
                            onClick={() => setIsPaymentFormOpen(true)}
                        >
                            {addLabel}
                        </ToolbarButton>
                    </Toolbar>
                </div>
            </div>

            <div className='paymentsList__divider' />

            {showEmpty ? (
                <div className='paymentsList__empty'>
                    <img
                        src={require('../../../../assets/img/nodata.png')}
                        alt='Sin datos'
                        className='paymentsList__emptyIcon'
                    />
                    <span className='paymentsList__emptyTitle'>
                        {isPayer ? 'No hay pagos registrados' : 'No hay cobros registrados'}
                    </span>
                    <span className='paymentsList__emptySubtitle'>
                        Agregá un nuevo {isPayer ? 'pago' : 'cobro'} con el botón "{addLabel}".
                    </span>
                </div>
            ) : (
                <div className='paymentsList__gridWrapper'>
                    <DataGrid
                        items={props.Payments}
                        columns={columns}
                        sortable
                        selectionMode='multiselect'
                        getRowId={(item) => item.id}
                        focusMode='composite'
                    >
                        <DataGridHeader>
                            <DataGridRow
                                selectionCell={{ checkboxIndicator: { 'aria-label': 'Seleccionar todas las filas' } }}
                            >
                                {({ renderHeaderCell }) => (
                                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                                )}
                            </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<Payment>>
                            {({ item, rowId }) => (
                                <DataGridRow<Payment>
                                    key={rowId}
                                    selectionCell={{ checkboxIndicator: { 'aria-label': 'Seleccionar fila' } }}
                                >
                                    {({ renderCell }) => (
                                        <DataGridCell>{renderCell(item)}</DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>
                </div>
            )}

            {props.isLoading && (
                <div className='paymentsList__loading'>
                    <Spinner size='extra-tiny' />
                    Cargando pagos…
                </div>
            )}

            {Forms}

            {/* Ver imputaciones */}
            <PaymentImputationsViewComponent
                paymentId={selectedViewPayment?.id ?? null}
                isOpen={selectedViewPayment !== null}
                onClose={() => setSelectedViewPayment(null)}
                title={`Imputaciones del ${isPayer ? 'Pago' : 'Cobro'}`}
            />

            {/* Imputar saldo pendiente */}
            <PaymentImputationFormComponent
                payment={selectedImputationPayment}
                isOpen={selectedImputationPayment !== null}
                onClose={() => setSelectedImputationPayment(null)}
                title='Imputar saldo pendiente'
            />
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PaymentListComponent as any);
